import { mergeWith } from 'lodash';
import { PaymentGateways, SubscriptionStatus } from '../enums';
import { getProductByProductId, onlyPlanSubscriptions } from '../utils/products';
import { sortSubscriptionsByDate } from '../utils/parsers';
import { buildCloudPayQuery } from '../utils/query';

const mergeSubscriptionsUpdate = (prev = {}, next = {}) =>
  mergeWith({}, prev, next, (_objValue, srcValue) =>
    Array.isArray(srcValue) ? srcValue : undefined
  );

/**
 * @description Send the transaction to the CRM to be processed and charge to the user payment method
 *
 * @param {Object} [Request options]
 * @returns {QueryConfig}
 */
export const sendTransactionRequest = buildCloudPayQuery(
  ({ transactionType, paymentMethod, data }) => ({
    force: true,
    url: `/subscription/${paymentMethod}/purchase`,
    options: {
      method: 'POST'
    },
    body: {
      transactionType,
      data: {
        ...data,
      },
    },
    transform: () => {
      if (transactionType !== 'SUBSCRIPTION') {
        return {};
      }

      return {
        subscriptions: {
          buySubscriptions: true
        }
      };
    },
    update: {
      subscriptions: mergeSubscriptionsUpdate
    }
  })
);

/**
 * @description put in state PENDING_COMPLETION actual user plan subscription
 *
 * @param {Object} [Request options]
 * @returns {QueryConfig}
 */
export const cancelPlanSubscription = buildCloudPayQuery(
  ({ methodId, productId, addOnSubscriptions, planSubscription, products, expireNow }) => ({
    force: true,
    url: `/subscription/${methodId}/plan/${productId}/cancel${expireNow ? '?expireNow=true' : ''}`,
    transform: ({ canceledProducts, paymentMethod }) => {
      const catalog = products || [];
      const isEventAddOn = addOn =>
        Boolean(addOn.subscriptionState?.event) ||
        Boolean(getProductByProductId(addOn.productId, catalog)?.event);

      const isCanceledPendingCompletion = canceledEntry =>
        canceledEntry?.subscriptionStatus === SubscriptionStatus.PENDING_COMPLETION;

      const findCanceledPendingCompletionByProductId = productId =>
        canceledProducts?.find(
          p => p.productId === productId && isCanceledPendingCompletion(p)
        );

      const isPendingCanceledAddOn = addOn =>
        Boolean(findCanceledPendingCompletionByProductId(addOn.productId));

      const subscriptionsUpdated = addOnSubscriptions?.map(sub => {
        const subscriptionPendingCanceled = findCanceledPendingCompletionByProductId(
          sub.productId
        );
        if (subscriptionPendingCanceled) {
          return {
            ...sub,
            subscriptionState: {
              ...sub.subscriptionState,
              status: SubscriptionStatus.PENDING_COMPLETION,
            },
          };
        }
        return sub;
      });

      const isPlanCanceled = canceledProducts?.some(
        sub =>
          sub.productId === planSubscription.productId &&
          isCanceledPendingCompletion(sub)
      );
      const planSubscriptionNext = isPlanCanceled
        ? {
            ...planSubscription,
            subscriptionState: {
              ...planSubscription.subscriptionState,
              status: SubscriptionStatus.PENDING_COMPLETION,
            },
          }
        : planSubscription;

      const addOnsNext = sortSubscriptionsByDate(subscriptionsUpdated || []).filter(
        addOn =>
          isPendingCanceledAddOn(addOn) || !isEventAddOn(addOn)
      );

      const mainPlan = canceledProducts.find(sub => sub.mainSubscription);
      const activePlan = paymentMethod === PaymentGateways.freepass && mainPlan.subscriptionStatus === SubscriptionStatus.EXPIRED ? null : planSubscriptionNext;

      return {
        subscriptions: {
          plan: [planSubscriptionNext],
          addOns: addOnsNext,
          activePlan,
        },
      };
    },
    update: {
      subscriptions: mergeSubscriptionsUpdate,
    },
    options: {
      method: 'GET'
    }
  })
);
/**
 * Reactivate a products (plan or addOn) subscription, sets its status in ONGOING
 *
 * @param {Object} [options]
 * @returns {QueryConfig}
 */
export const reactivateProducts = buildCloudPayQuery(
  ({ action, token, addOnSubscriptions, products, planSubscription, methodId, ...data }) => {
    const endpoints = {
      plan: {
        route: 'plan/reactivate',
        body: { reactivatePlans: data.reactivatePlans }
      },
      addOn: {
        route: 'addOn/reactivate',
        body: { reactivateAddOns: data.reactivateAddOns }
      }
    }

    const { route, body } = endpoints[action];

    return ({
      force: true,
      body,
      url: `/subscription/${methodId}/${route}`,
      transform: ({ purchasedProducts, canceledProducts }) => {
        const subscriptionsUpdated = addOnSubscriptions?.map(sub => {
          const subscriptionReactivated = purchasedProducts.find(productSub => productSub.productId === sub.productId);
          const subscriptionSwitched = canceledProducts.find(productSub => productSub.productId === sub.productId);
          if (subscriptionSwitched) {
            sub.subscriptionState.status = SubscriptionStatus.REVOKED;
            return sub;
          }
          if (subscriptionReactivated) {
            sub.subscriptionState.status = SubscriptionStatus.ONGOING;
            return sub;
          }
          return sub;
        });
        const isPlanRenewed = purchasedProducts.some(sub => sub.productId === planSubscription.productId);
        if (isPlanRenewed) {
          planSubscription.subscriptionState.status = SubscriptionStatus.ONGOING;
        }
        return {
          subscriptions: {
            addOns: sortSubscriptionsByDate(subscriptionsUpdated),
            plan: [planSubscription]
          }
        };
      },
      update: {
        subscriptions: mergeSubscriptionsUpdate,
      },
      options: {
        authorization: 'Bearer ' + token,
        method: 'PATCH'
      },
    })
  }
)
/**
 * Handle plan change subscription, puts the previous plan in PENDING_COMPLETION and the current one in ONGOING
 *
 * @param {Object} [options]
 * @returns {QueryConfig}
 */
export const updatePlanSubscription = buildCloudPayQuery(
  ({ token, subscriptions, methodId, products, paymentInfo, ...data }) => {
    return ({
      force: true,
      body: { ...paymentInfo, favorite: true },
      url: `/subscription/${methodId}/plan/update`,
      transform: ({ purchasedProducts, canceledProducts, externalId }) => {
        // updated subscriptions.plan (for show in myAccount)
        let planToShow = null;
        if (canceledProducts[0].urns.length > purchasedProducts[0].urns.length ||
        (canceledProducts[0].urns.length === purchasedProducts[0].urns.length && canceledProducts[0].expiresAt > purchasedProducts[0].expiresAt)) {
          planToShow = canceledProducts[0]
        }
        const planUpdated = [{
          productId: purchasedProducts[0].productId,
          expiresAt: purchasedProducts[0].expiresAt,
          productType: 'plan',
          subscriptionState: {
            status: SubscriptionStatus.ONGOING
          },
        }];
        // updated subscriptions list and add new plan to state
        const updatedSubscriptions = subscriptions.map(sub => {
          if (canceledProducts.some(p => p.productId === sub.productId)) {
            return {
              ...sub,
              status: SubscriptionStatus.PENDING_COMPLETION
            };
          }
          if (purchasedProducts.some(p => p.productId === sub.productId)) {
            return {
              ...sub,
              status: SubscriptionStatus.ONGOING
            };
          }
          return sub;
        });
        if (!updatedSubscriptions.some(sub => sub.productId === planUpdated[0].productId)) {
          updatedSubscriptions.push(planUpdated[0]);
        }
        return {
          subscriptions: {
            all: updatedSubscriptions,
            plan: onlyPlanSubscriptions(updatedSubscriptions, products),
            activePlan: {...planUpdated[0], purchaseState: {externalId}},
            planToShow
          }
        }
      },
      update: {
        subscriptions: mergeSubscriptionsUpdate,
      },
      options: {
        authorization: 'Bearer ' + token,
        method: 'PATCH'
      },
    })
  }
);

/**
 * Handle upgrade addOn subscription, puts the news subscription in addOnSubscription entitie with ONGOING status
 *
 * @param {Object} [options]
 * @returns {QueryConfig}
 */
export const upgradeAddOnSubscriptions = buildCloudPayQuery(
  ({ token, addOnSubscriptions, methodId, products, paymentInfo, ...data }) => {
    return ({
      force: true,
      body: paymentInfo,
      url: `/subscription/${methodId}/addOn/upgrade`,
      transform: ({ purchasedProducts, canceledProducts }) => {
        const addOns = addOnSubscriptions?.length > 0 ? addOnSubscriptions : [];
        const newAddOn = purchasedProducts.reduce((acc, subscription) => {
          const product = products.find(p => p.productID === subscription.productId);
          const newSubscription = {
            productId: subscription.productId,
            subscriptionState: {
              status: SubscriptionStatus.ONGOING,
              ...(product?.event && { event: true }),
              updatedAt: new Date().toISOString()
            },
            createdAt: new Date().toISOString(),
            expiresAt: subscription.expiresAt,
            productType: product.productType
          };
          acc.push(newSubscription);
          return acc;
        }, []);
        const onlyActivesSubscriptions = addOns.filter(subscription => !(canceledProducts?.some(p => p.productId === subscription.productId)));
        const updatedAddOnsSubscriptions = onlyActivesSubscriptions.length > 0 ? onlyActivesSubscriptions.concat(...newAddOn) : newAddOn;

        return {
          subscriptions: {
            addOns: sortSubscriptionsByDate(updatedAddOnsSubscriptions),
            all: sortSubscriptionsByDate(updatedAddOnsSubscriptions)
          }
        };
      },
      update: {
        subscriptions: mergeSubscriptionsUpdate,
      },
      options: {
        authorization: 'Bearer ' + token,
        method: 'PATCH'
      },
    })
  }
);
/**
 * Handle downgrade addOn subscription, puts its status in PENDING_COMPLETION
 *
 * @param {Object} [options]
 * @returns {QueryConfig}
 */
export const downgradeAddOnSubscriptions = buildCloudPayQuery(
  ({ token, addOnSubscriptions, planSubscription, methodId, products, ...data }) => {
    return ({
      force: true,
      body: { cancelAddOns: data.cancelAddOns },
      url: `/subscription/${methodId}/addOn/downgrade`,
      transform: ({ canceledProducts }) => {
        const addOnSubscriptionsUpdated = addOnSubscriptions.map(sub => {
          const subscriptionPendingCanceled = canceledProducts?.find(productSub => productSub.productId === sub.productId);
          if (subscriptionPendingCanceled) {
            sub.subscriptionState.status = SubscriptionStatus.PENDING_COMPLETION;
            return sub;
          }
          return sub;
        });
        return {
          subscriptions: {
            addOns: sortSubscriptionsByDate(addOnSubscriptionsUpdated)
          }
        };
      },
      update: {
        subscriptions: mergeSubscriptionsUpdate,
      },
      options: {
        authorization: 'Bearer ' + token,
        method: 'PATCH'
      },
    })
  }
)